import { useMemo } from 'react';
import { BaseLayout, type NavSection } from './BaseLayout';
import {
  LayoutDashboard, Users, GraduationCap, ClipboardList, ShieldAlert,
  DoorOpen, Megaphone, Heart, BarChart3, Shield, Clock, BookOpen, UserCheck, MessageSquare, FileText, UserCog,
  UserMinus, FolderOpen, User, Bell,
} from 'lucide-react';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { useSetupBadges } from '../../hooks/useSetupBadges';
import { useAuthStore } from '../../store/authStore';
import { useProfileBrandColor } from '../../theme/useProfileBrandColor';
import type { BrandProfileKey } from '../../theme/profileBrandColors';

// ═══════════════════════════════════════════════
// Seções estáticas
// ═══════════════════════════════════════════════

const SECTION_DASHBOARD: NavSection = {
  title: null,
  items: [
    { to: '/equipe/dashboard', label: 'Dashboard', icon: LayoutDashboard, badgeKey: 'dashboard' },
    { to: '/equipe/ead', label: 'Video Aulas', icon: GraduationCap },
  ],
};

// Setup: itens com badgeKey para exibir ✓ / PRÓXIMO / 🔒
const SECTION_CONFIG: NavSection = {
  title: 'Configuração',
  items: [
    { to: '/equipe/turnos', label: 'Turnos', icon: Clock, badgeKey: 'turnos' },
    { to: '/equipe/equipe', label: 'Equipe', icon: UserCog, badgeKey: 'equipe' },
    { to: '/equipe/materias', label: 'Matérias', icon: BookOpen, badgeKey: 'materias' },
    { to: '/equipe/turmas', label: 'Turmas', icon: GraduationCap, badgeKey: 'turmas' },
    { to: '/equipe/professores', label: 'Professores', icon: UserCheck, badgeKey: 'professores' },
    { to: '/equipe/alunos', label: 'Alunos', icon: Users, badgeKey: 'alunos' },
  ],
};

/** Menu operacional da secretaria — agrupado (opção B: Cobertura nos dois painéis). */
const SECTION_PESSOAS: NavSection = {
  title: 'Pessoas',
  alwaysExpanded: true,
  items: [
    { to: '/equipe/alunos', label: 'Alunos', icon: Users },
    { to: '/equipe/responsaveis', label: 'Responsáveis', icon: Heart },
    { to: '/equipe/equipe', label: 'Equipe', icon: UserCog },
    { to: '/equipe/conversas-pais', label: 'Atendimento (Pais)', icon: MessageSquare },
  ],
};

const SECTION_FREQUENCIA: NavSection = {
  title: 'Frequência & Turmas',
  alwaysExpanded: true,
  items: [
    { to: '/equipe/turmas', label: 'Turmas', icon: GraduationCap },
    { to: '/equipe/frequencia', label: 'Frequência', icon: ClipboardList },
    { to: '/equipe/justificativas', label: 'Justificativas de Falta', icon: FileText },
    { to: '/equipe/chamadas-gestao', label: 'Governança Chamadas', icon: Shield },
  ],
};

const SECTION_OPERACAO: NavSection = {
  title: 'Operação do Dia',
  alwaysExpanded: true,
  items: [
    { to: '/equipe/cobertura', label: 'Cobertura de Grade', icon: UserMinus },
    { to: '/equipe/portaria', label: 'Portaria', icon: DoorOpen },
    { to: '/equipe/incidentes', label: 'Incidentes', icon: ShieldAlert },
    { to: '/equipe/comunicados', label: 'Comunicados', icon: Megaphone },
    { to: '/equipe/midias', label: 'Midias', icon: FolderOpen },
  ],
};

const SECTION_APOIO: NavSection = {
  title: 'Apoio',
  alwaysExpanded: true,
  items: [
    { to: '/equipe/relatorios', label: 'Relatórios', icon: BarChart3 },
  ],
};

const SECTION_MINHA_CONTA: NavSection = {
  title: 'Minha Conta',
  items: [
    { to: '/equipe/perfil', label: 'Meu Perfil', icon: User },
    { to: '/equipe/alertas', label: 'Meus Comunicados', icon: Megaphone },
  ],
};

// ═══════════════════════════════════════════════
// Layout Dinâmico
// ═══════════════════════════════════════════════

export function EquipeLayout() {
  const cargo = useAuthStore((s) => s.cargo);
  // No auth/sim o cargo costuma ser COORDENACAO (não COORDENADOR) — sem isso herda cor da Secretaria
  const cargoNorm = (cargo || '').toUpperCase();
  const brandKey: BrandProfileKey =
    cargoNorm === 'COORDENADOR' || cargoNorm === 'COORDENACAO' || cargoNorm.includes('COORD')
      ? 'COORDENADOR'
      : 'SECRETARIA';
  const { brandColor, brandGradient } = useProfileBrandColor(brandKey);

  // prontoParaOperar: visão GLOBAL (sem cargo) — escola só está pronta quando TODAS as etapas concluídas
  const { prontoParaOperar } = useSetupStatus({ routePrefix: '/equipe' });

  // badges: filtrados por cargo — cada perfil vê badges das etapas sob sua responsabilidade
  const badges = useSetupBadges({ routePrefix: '/equipe', cargo: cargo || undefined });

  const sections = useMemo<NavSection[]>(() => {
    const list = [
      SECTION_DASHBOARD,
      SECTION_PESSOAS,
      SECTION_FREQUENCIA,
      SECTION_OPERACAO,
      SECTION_APOIO,
      SECTION_MINHA_CONTA,
    ];
    if (!prontoParaOperar) {
      list.splice(1, 0, SECTION_CONFIG);
    }
    return list;
  }, [prontoParaOperar]);

  return (
    <BaseLayout
      sections={sections}
      headerTitle="Painel Equipe"
      brandColor={brandColor}
      brandGradient={brandGradient}
      badges={badges}
    />
  );
}
